import { useState, useEffect } from 'react';
import FieldTypes from '../FieldTypes';
import API from '../../../API';
import styles from './AddLog.module.css';

const AddLog = (props) => {
    const { id, fields, qrType, qrData, setShowAddLog, editLog, setEditLog, configTypes, authenticatedUser } = props;

    const { addTableLog, updateTableLog, getSignature } = API;
    const [error, setError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [sigCanvas, setSigCanvas] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [newLog, setNewLog] = useState(fields.reduce((acc, field) => {
        if (field?.archived) return acc;
        if (editLog && editLog?.hasOwnProperty(field.id)) {
            acc[field.id] = editLog[field.id];
            return acc;
        };

        if (configTypes?.[field?.type]?.type === 'checkbox' && configTypes?.[field?.type]?.multiple) {
            acc[field.id] = [];
        } else if (configTypes?.[field?.type]?.type === 'address') {
            acc[field.id] = { street: '', city: '', state: '', postalCode: '' };
        } else {
            acc[field.id] = '';
        }
        return acc;
    }, {}));

    useEffect(() => {
        if (editLog && fields?.find(f => configTypes?.[f?.type]?.type === 'signature') && sigCanvas) {
            const handleLogSig = async () => {
                try {
                    const res = await getSignature(id, editLog.date);
                    if (res) sigCanvas.fromData(res);
                } catch (err) {
                    console.error(err);
                };
            };

            handleLogSig();
        };
    }, [editLog, fields, sigCanvas, id]);

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone) => {
        const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
        return phone.length >= 10 && phoneRegex.test(phone);
    };

    const handleAddLog = async () => {
        const logValid = fields?.reduce((acc, cur) => {
            if (!acc) return acc;

            if (cur?.required) {
                switch (configTypes[cur?.type].type) {
                    case 'date':
                        break;
                    case 'email':
                        acc = newLog[cur.id]?.length > 0 && isValidEmail(newLog[cur.id]) ? true : false;
                        break;
                    case 'phone':
                        acc = newLog[cur.id]?.length > 0 && isValidPhone(newLog[cur.id]) ? true : false;
                        break;
                    case 'address':
                        acc = newLog[cur.id]?.street || newLog[cur.id]?.city || newLog[cur.id]?.state || newLog[cur.id]?.postalCode ? true : false;
                        break;
                    case 'signature':
                        if (editLog && editLog?.hasOwnProperty(cur.id)) {
                            break;
                        };

                        acc = (!sigCanvas?.isEmpty() ? true : false);
                        break;
                    case 'num-rating':
                    case 'icon-rating':
                        acc = newLog[cur.id] > 1 ? true : false;
                        break;
                    case 'slide-scale':
                        acc = (newLog[cur.id] !== null && newLog[cur.id] !== '-' && newLog[cur.id] !== '' && newLog[cur.id] >= 0 && newLog[cur.id] <= 100) ? true : false;
                        break;
                    case 'checkbox':
                        if (configTypes[cur?.type]?.multiple === true) {
                            acc = Array.isArray(newLog[cur.id]) && newLog[cur.id].length > 0;
                            break;
                        } else {
                            acc = newLog[cur.id];
                            break;
                        };
                    case 'file':
                        acc = newLog[cur.id]?.url ? true : false;
                        break;
                    default:
                        acc = (newLog[cur.id]?.length > 0 ? true : false);
                        break;
                };
            };

            return acc;
        }, true);

        if (!logValid) {
            setError('Please fill out all required fields and ensure all emails and phone numbers are valid');
            return;
        };

        const newDate = Date.now();
        newLog.date = (editLog && editLog?.hasOwnProperty('date')) ? editLog.date : newDate;
        newLog.date_edited = newDate;
        let sigArr = [];

        if (fields?.find(field => configTypes?.[field?.type]?.type === 'signature')) {
            const field = fields?.find(f => configTypes?.[f?.type]?.type === 'signature');
            if (field?.required && sigCanvas?.isEmpty() && !editLog) {
                setError('Please sign before submitting');
                return;
            } else if (sigCanvas?.isEmpty()) {
                sigArr = [];
                newLog[field.id] = false;
            } else {
                sigArr = sigCanvas.toData();
                newLog[field.id] = true;
            };
        };

        try {
            // Filter out readOnly fields before saving
            const logDataToSave = Object.keys(newLog).reduce((acc, key) => {
                const field = fields.find(f => f.id === key);
                const isReadOnly = field && configTypes?.[field?.type]?.readOnly;
                
                if (!isReadOnly) {
                    acc[key] = newLog[key];
                }
                
                return acc;
            }, {});

            if (editLog && Object?.keys(editLog)?.length > 0) {
                const res = await updateTableLog(id, logDataToSave, qrType);
                setEditLog(res);
            } else {
                await addTableLog(id, logDataToSave, sigArr, qrType);
            };
            handleReset();
        } catch (err) {
            console.error(err);
        };
    };

    const requiredValid = fields?.reduce((acc, cur) => {
        if (!acc) return acc;

        if (cur?.required) {
            switch (configTypes[cur?.type]?.type) {
                case 'date':
                    break;
                case 'email':
                    acc = newLog[cur.id]?.length > 0 && isValidEmail(newLog[cur.id]) ? true : false;
                    break;
                case 'phone':
                    acc = newLog[cur.id]?.length > 0 && isValidPhone(newLog[cur.id]) ? true : false;
                    break;
                case 'address':
                    acc = newLog[cur.id]?.street || newLog[cur.id]?.city || newLog[cur.id]?.state || newLog[cur.id]?.postalCode ? true : false;
                    break;
                case 'signature':
                    if (editLog && editLog?.hasOwnProperty(cur.id)) {
                        break;
                    };

                    acc = (!sigCanvas?.isEmpty() ? true : false);
                    break;
                case 'num-rating':
                case 'icon-rating':
                    acc = newLog[cur.id] > 1 ? true : false;
                    break;
                case 'slide-scale':
                    acc = (newLog[cur.id] !== null && newLog[cur.id] !== '-' && newLog[cur.id] !== '' && newLog[cur.id] >= 0 && newLog[cur.id] <= 100) ? true : false;
                    break;
                case 'checkbox':
                    if (configTypes[cur?.type]?.multiple === true) {
                        acc = Array.isArray(newLog[cur.id]) && newLog[cur.id].length > 0;
                        break;
                    } else {
                        acc = newLog[cur.id];
                        break;
                    };
                case 'file':
                    acc = newLog[cur.id]?.url ? true : false;
                    break;
                default:
                    acc = (newLog[cur.id]?.length > 0 ? true : false);
                    break;
            };
        };

        return acc;
    }, true);
    const handleReset = () => {
        (sigCanvas && !editLog) && sigCanvas.clear();
        setNewLog(fields.reduce((acc, field) => {
            if (field?.archived) return acc;

            if (configTypes?.[field?.type]?.type === 'checkbox' && configTypes?.[field?.type]?.multiple) {
                acc[field.id] = [];
            } else if (configTypes?.[field?.type]?.type === 'address') {
                acc[field.id] = { street: '', city: '', state: '', postalCode: '' };
            } else {
                acc[field.id] = '';
            }
            return acc;
        }, {}));
        setDisabled(false);
        
        if (qrType === 'survey' && !authenticatedUser) {
            if (qrData?.post_survey_options?.url) {
                window.location.assign(qrData?.post_survey_options?.url?.includes('http') ? qrData?.post_survey_options?.url : `https://${qrData?.post_survey_options?.url}`);
            };
            setSubmitted(true);
        } else {
            setShowAddLog(false);
        };
    };

    if (qrType === 'survey' && submitted) {
        return (
            <div className={styles.card}>
                <div className={styles.header}>{qrData?.post_survey_options?.title ? qrData.post_survey_options.title : 'Thanks!'}</div>
                <div className={styles.body}>
                    <div className={styles.envelopeWrap}>
                        <i className={`fas fa-check ${styles.checkIcon}`} />
                        <i className={`fa-duotone fas fa-hands-holding-diamond ${styles.handsIcon}`} />
                    </div>
                    <div className={styles.message}>{qrData?.post_survey_options?.message ? qrData.post_survey_options.message : 'Submission received!'}</div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.addLogContainer}>
            <div className={styles.typesContainer}>
                {fields?.filter(field => !field?.archived)?.map((field, index) => (
                    <FieldTypes
                        key={index}
                        field={field}
                        state={newLog}
                        setState={setNewLog}
                        sigCanvas={sigCanvas}
                        setSigCanvas={setSigCanvas}
                        edit={(editLog && Object?.keys(editLog)?.length) ? true : false}
                        configTypes={configTypes}
                        qrId={id}
                        logId={newLog?.date || editLog?.date || Date.now()}
                    />
                ))}
            </div>
            <div className={styles.btnContainer}>
                <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                    <button className={`${styles.btn} ${styles.cancel} me-2`} onClick={handleReset}>Cancel</button>
                    <button
                        className={`${styles.save} ${styles.btn} ms-2`}
                        onClick={() => { setDisabled(() => true); handleAddLog(); }}
                        disabled={!newLog || disabled || !requiredValid}
                    >{disabled ? <i className='fa-duotone fa-spinner fa-spin' /> : 'Save'}</button>
                </div>
                <div style={{ color: 'red' }}><i className='far fa-asterisk me-1' style={{ fontSize: '.9rem' }} />- Required</div>
                {error ? <p className={styles.error}>{error}</p> : <></>}
            </div>
        </div>
    );
};

export default AddLog;